/**
 * New-release detection for the soft "new version available" banner.
 *
 * The running bundle bakes its release id into `__APP_BUILD_ID__`. The deployed
 * `version.json` (served `no-cache` from the app origin) always reflects the
 * latest release. Comparing the two tells an already-open tab that a newer
 * version exists — without ever forcing a reload (durable assets on R2 keep the
 * old tab working until the user chooses to refresh).
 */

export const VERSION_ENDPOINT = '/version.json';

/** Build id compiled into this bundle. Empty string in unexpected setups. */
export function getCurrentBuildId(): string {
  return typeof __APP_BUILD_ID__ === 'string' ? __APP_BUILD_ID__ : '';
}

/**
 * True when `remote` denotes a different release than the one running, and both
 * ids are usable. A missing/empty current id (dev, misconfig) never prompts.
 */
export function isNewerBuild(current: string, remote: string | null): boolean {
  if (!current || !remote) {
    return false;
  }
  return remote !== current;
}

/** Extracts the build id from a parsed version.json payload, if present. */
export function parseBuildId(payload: unknown): string | null {
  if (
    typeof payload === 'object' &&
    payload !== null &&
    'buildId' in payload &&
    typeof (payload as { buildId: unknown }).buildId === 'string'
  ) {
    const buildId = (payload as { buildId: string }).buildId;
    return buildId.length > 0 ? buildId : null;
  }
  return null;
}

/**
 * Fetches the deployed build id. Returns null on any failure (offline, 404 in
 * dev, malformed payload) — callers treat null as "no update info available".
 */
export async function fetchRemoteBuildId(
  signal?: AbortSignal,
): Promise<string | null> {
  try {
    const response = await fetch(VERSION_ENDPOINT, {
      cache: 'no-store',
      signal,
    });
    if (!response.ok) {
      return null;
    }
    const payload = (await response.json()) as unknown;
    return parseBuildId(payload);
  } catch {
    return null;
  }
}
